import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import AdminShell, { eur } from "./AdminShell";

const API = config.API_BASE_URL;

const PRODUCT_LABELS = { estiasync_buzz: "Buzz", ordering: "Ordering" };

function Kpi({ label, value }) {
  return (
    <div className="ad-kpi">
      <div className="v">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

// CSS bar chart — ίδια προσέγγιση με BuzzReports (χωρίς chart library).
function BarChart({ title, points, money }) {
  const max = Math.max(1, ...points.map((p) => p.v));
  return (
    <div className="ad-card">
      <h3>{title}</h3>
      {points.length === 0 ? (
        <div className="ad-hint">—</div>
      ) : (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 110 }}>
          {points.map((p) => (
            <div
              key={p.d}
              title={`${new Date(p.d).toLocaleDateString("el-GR")} — ${money ? eur(p.v) : p.v}`}
              style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}
            >
              <div
                style={{
                  height: `${(p.v / max) * 100}%`,
                  background: money ? "#047857" : "#2563eb",
                  borderRadius: 2,
                  minHeight: p.v ? 3 : 0,
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Γεμίζει τις μέρες που λείπουν με 0 ώστε ο άξονας να είναι συνεχής.
function fillDays(points, days) {
  const map = new Map(points.map((p) => [String(p.d).slice(0, 10), p.v]));
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    const dte = new Date();
    dte.setDate(dte.getDate() - i);
    const key = dte.toISOString().slice(0, 10);
    out.push({ d: key, v: map.get(key) || 0 });
  }
  return out;
}

export default function AdminOverview() {
  const { t } = useTranslation();
  const [ov, setOv] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    axios.get(`${API}/admin/overview`).then((r) => setOv(r.data)).catch(() => {});
  }, []);

  const loadMetrics = useCallback(() => {
    axios.get(`${API}/admin/metrics`, { params: { days } }).then((r) => setMetrics(r.data)).catch(() => {});
  }, [days]);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  if (!ov) return <AdminShell><div className="ad-empty">…</div></AdminShell>;
  const k = ov.kpis;

  const rollup = {};
  for (const r of ov.productRollup || []) {
    rollup[r.product_code] = rollup[r.product_code] || {};
    rollup[r.product_code][r.status] = r.c;
  }

  return (
    <AdminShell>
      <div className="ad-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", marginBottom: 14 }}>
        <Kpi label={t("Customers")} value={k.companies} />
        <Kpi label={t("Users")} value={k.users} />
        <Kpi label={t("Active core subscriptions")} value={k.coreActiveSubs} />
        <Kpi label={t("Core MRR (estimate)")} value={eur(k.coreMrrEur)} />
        <Kpi label={t("Payments (30d)")} value={eur(k.payments30dEur)} />
        <Kpi label={t("Reservations (30d)")} value={k.reservations30d} />
        <Kpi label={t("Buzz calls (30d)")} value={k.buzzCalls30d} />
        <Kpi label={t("Orders (30d)")} value={k.orders30d} />
      </div>

      <div className="ad-card" style={{ marginBottom: 14 }}>
        <h3>{t("Products & services")}</h3>
        {Object.keys(rollup).length === 0 ? (
          <div className="ad-hint">—</div>
        ) : (
          Object.entries(rollup).map(([code, byStatus]) => (
            <div key={code} style={{ marginBottom: 4 }}>
              <strong style={{ fontSize: 13 }}>{PRODUCT_LABELS[code] || code}</strong>{" "}
              {Object.entries(byStatus).map(([s, c]) => (
                <span key={s} className={`ad-chip ${s}`}>{t(s)}: {c}</span>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="ad-toolbar">
        {[30, 90].map((d) => (
          <button key={d} className={`ad-btn ${days === d ? "primary" : ""}`} onClick={() => setDays(d)}>
            {d} {t("days")}
          </button>
        ))}
      </div>
      {metrics && (
        <div className="ad-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
          <BarChart title={t("Revenue")} points={fillDays(metrics.series.revenue, metrics.days)} money />
          <BarChart title={t("Reservations")} points={fillDays(metrics.series.reservations, metrics.days)} />
          <BarChart title={t("Buzz calls")} points={fillDays(metrics.series.buzzCalls, metrics.days)} />
          <BarChart title={t("Orders")} points={fillDays(metrics.series.orders, metrics.days)} />
          <BarChart title={t("New customers")} points={fillDays(metrics.series.newCompanies, metrics.days)} />
        </div>
      )}
    </AdminShell>
  );
}
